import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CartContext = createContext(null);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const { token } = useAuth();
  const [cartItems, setCartItems] = useState([]);
  const [cartTotal, setCartTotal] = useState(0);
  const [itemCount, setItemCount] = useState(0);
  const [freeDelivery, setFreeDelivery] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem('cart_session_id') || null);

  // Track previous token to detect login/logout transitions
  const prevTokenRef = useRef(token);

  const resetCartState = () => {
    setCartItems([]);
    setCartTotal(0);
    setItemCount(0);
    setFreeDelivery(false);
  };

  const fetchCart = useCallback(async () => {
    const currentToken = localStorage.getItem('auth_token');
    const storedSessionId = localStorage.getItem('cart_session_id');

    // No auth and no guest session → empty cart, don't hit the API
    if (!currentToken && !storedSessionId) {
      resetCartState();
      setInitialized(true);
      return;
    }

    setLoading(true);
    try {
      const params = storedSessionId && !currentToken ? { session_id: storedSessionId } : {};
      const response = await axios.get(`${API}/cart`, {
        headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {},
        params,
      });
      setCartItems(response.data.items || []);
      setCartTotal(response.data.total || 0);
      setItemCount(response.data.item_count || 0);
      setFreeDelivery(response.data.free_delivery || false);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
      setInitialized(true);
    }
  }, []);

  // Merge guest cart into user cart on login, then refresh
  const mergeGuestCart = useCallback(async (authToken) => {
    const storedSessionId = localStorage.getItem('cart_session_id');
    if (!authToken || !storedSessionId) return;
    try {
      await axios.post(
        `${API}/cart/merge`,
        null,
        {
          headers: { Authorization: `Bearer ${authToken}` },
          params: { session_id: storedSessionId },
        }
      );
      // Guest session has been absorbed — drop it
      localStorage.removeItem('cart_session_id');
      setSessionId(null);
    } catch (error) {
      console.error('Error merging guest cart:', error);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchCart();
  }, [fetchCart]);

  // React to token changes (login/logout) within this tab
  useEffect(() => {
    const prev = prevTokenRef.current;
    prevTokenRef.current = token;

    if (prev === token) return;

    const run = async () => {
      if (!prev && token) {
        // Just logged in → merge guest cart into user
        await mergeGuestCart(token);
      } else if (prev && !token) {
        // Just logged out → clear view so we don't show stale user items
        resetCartState();
      }
      await fetchCart();
    };
    run();
  }, [token, mergeGuestCart, fetchCart]);

  // Cross-tab sync: if another tab logs in/out or changes the guest session,
  // refresh our local state.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'auth_token' || e.key === 'cart_session_id') {
        fetchCart();
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, [fetchCart]);

  const addToCart = async (item) => {
    setLoading(true);
    try {
      const currentToken = localStorage.getItem('auth_token');
      const storedSessionId = localStorage.getItem('cart_session_id');
      const params = storedSessionId && !currentToken ? { session_id: storedSessionId } : {};
      const response = await axios.post(`${API}/cart/add`, item, {
        headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {},
        params,
      });

      // Persist new guest session id returned by the backend
      if (response.data.session_id && !currentToken) {
        localStorage.setItem('cart_session_id', response.data.session_id);
        setSessionId(response.data.session_id);
      }

      await fetchCart();
      return response.data;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const removeFromCart = async (itemId) => {
    setLoading(true);
    try {
      const currentToken = localStorage.getItem('auth_token');
      const storedSessionId = localStorage.getItem('cart_session_id');
      const params = storedSessionId && !currentToken ? { session_id: storedSessionId } : {};
      await axios.delete(`${API}/cart/${itemId}`, {
        headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {},
        params,
      });
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (itemId, quantity) => {
    setLoading(true);
    try {
      const currentToken = localStorage.getItem('auth_token');
      const storedSessionId = localStorage.getItem('cart_session_id');
      const params = storedSessionId && !currentToken ? { session_id: storedSessionId } : {};
      await axios.put(`${API}/cart/${itemId}?quantity=${quantity}`, null, {
        headers: currentToken ? { Authorization: `Bearer ${currentToken}` } : {},
        params,
      });
      await fetchCart();
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const clearCart = () => {
    resetCartState();
    localStorage.removeItem('cart_session_id');
    setSessionId(null);
  };

  const value = {
    cartItems,
    cartTotal,
    itemCount,
    freeDelivery,
    loading,
    initialized,
    sessionId,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    fetchCart,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

export default CartContext;
